import { ReactNode, useRef, useState, useCallback } from 'react';
import {
  ExpandOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { Flex, theme, type GlobalToken } from 'antd';
import styled from 'styled-components';
import { IconButton } from '../IconButton';
import { radius } from '../../theme/radius';

export type PdfZoomControlsProps = {
  /** Callback when zoom in button is clicked */
  onZoomIn: () => void;
  /** Callback when zoom out button is clicked */
  onZoomOut: () => void;
  /** Callback when zoom to fit button is clicked */
  onZoomToFit: () => void;
  /** Whether to show the controls (controlled mode) */
  visible?: boolean;
  /** Auto-hide delay in milliseconds (default: 3000) */
  autoHideDelay?: number;
  /** Custom class name */
  className?: string;
  /** Custom styles */
  style?: React.CSSProperties;
  /** Custom zoom in icon */
  zoomInIcon?: ReactNode;
  /** Custom zoom out icon */
  zoomOutIcon?: ReactNode;
  /** Custom zoom to fit icon */
  zoomToFitIcon?: ReactNode;
  /** Callback when mouse enters the controls (use with hook's preventHide) */
  onControlsMouseEnter?: () => void;
  /** Callback when mouse leaves the controls (use with hook's show to restart timer) */
  onControlsMouseLeave?: () => void;
};

const ZoomButton = ({
  icon,
  onClick,
  'aria-label': ariaLabel,
}: {
  icon: ReactNode;
  onClick: () => void;
  'aria-label'?: string;
}) => {
  return (
    <IconButton
      onClick={onClick}
      icon={icon}
      variant="utility"
      size="m"
      shape="round"
      aria-label={ariaLabel}
    />
  );
};

const ZoomButtonsContainer = styled(Flex).withConfig({
  shouldForwardProp: (prop) => prop !== '$visible' && prop !== '$token',
})<{ $visible: boolean; $token: GlobalToken }>`
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px;
  position: absolute;
  top: 15px;
  right: 30px;
  z-index: 10;
  opacity: ${(props) => (props.$visible ? 0.5 : 0)};
  pointer-events: ${(props) => (props.$visible ? 'auto' : 'none')};
  transition: opacity 0.3s ease-in-out;
  border-radius: ${radius.pill}px;
  background: color-mix(
    in srgb,
    ${({ $token }) => $token.colorBgElevated} 90%,
    transparent
  );

  &:hover {
    opacity: 1;
  }

  button {
    background: transparent;
    box-shadow: none !important;

    &:hover:not(:disabled) {
      background: ${({ $token }) => $token.colorFillSecondary} !important;
    }

    &:active:not(:disabled) {
      background: ${({ $token }) => $token.colorFill} !important;
    }
  }
`;

/**
 * Floating zoom control buttons for PDF viewers.
 *
 * Provides zoom in, zoom out, and zoom to fit controls with auto-hide functionality.
 * Can be used in controlled mode (passing `visible` prop) or uncontrolled mode
 * (auto-hides after `autoHideDelay`).
 *
 * @param props - Component props
 * @returns Floating zoom control panel
 *
 * @example
 * ```tsx
 * // Uncontrolled mode with auto-hide
 * <PdfZoomControls
 *   onZoomIn={() => setScale(s => s + 0.1)}
 *   onZoomOut={() => setScale(s => s - 0.1)}
 *   onZoomToFit={() => setScale(1)}
 * />
 *
 * // Controlled mode with external visibility
 * const { visible, show } = usePdfZoomControlsVisibility();
 * <div onMouseMove={show}>
 *   <PdfZoomControls visible={visible} ... />
 * </div>
 * ```
 */
export const PdfZoomControls = ({
  onZoomIn,
  onZoomOut,
  onZoomToFit,
  visible: controlledVisible,
  autoHideDelay = 3000,
  className,
  style,
  zoomInIcon = <ZoomInOutlined />,
  zoomOutIcon = <ZoomOutOutlined />,
  zoomToFitIcon = <ExpandOutlined />,
  onControlsMouseEnter,
  onControlsMouseLeave,
}: PdfZoomControlsProps) => {
  const { token } = theme.useToken();
  const [internalVisible, setInternalVisible] = useState(false);
  const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isControlled = controlledVisible !== undefined;
  const isVisible = isControlled ? controlledVisible : internalVisible;

  const clearHideTimeout = useCallback(() => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimeout();
    hideTimeout.current = setTimeout(() => {
      setInternalVisible(false);
    }, autoHideDelay);
  }, [autoHideDelay, clearHideTimeout]);

  const handleButtonsMouseEnter = useCallback(() => {
    if (!isControlled) {
      clearHideTimeout();
    }
    onControlsMouseEnter?.();
  }, [isControlled, clearHideTimeout, onControlsMouseEnter]);

  const handleButtonsMouseLeave = useCallback(() => {
    if (!isControlled) {
      scheduleHide();
    }
    onControlsMouseLeave?.();
  }, [isControlled, scheduleHide, onControlsMouseLeave]);

  return (
    <ZoomButtonsContainer
      $visible={isVisible}
      $token={token}
      className={className}
      style={style}
      onMouseEnter={handleButtonsMouseEnter}
      onMouseLeave={handleButtonsMouseLeave}
      data-testid="pdf-zoom-controls"
    >
      <ZoomButton icon={zoomInIcon} onClick={onZoomIn} aria-label="Zoom in" />
      <ZoomButton
        icon={zoomToFitIcon}
        onClick={onZoomToFit}
        aria-label="Zoom to fit"
      />
      <ZoomButton
        icon={zoomOutIcon}
        onClick={onZoomOut}
        aria-label="Zoom out"
      />
    </ZoomButtonsContainer>
  );
};

/**
 * Hook to manage PdfZoomControls visibility with auto-hide functionality.
 *
 * Use this hook when you need to control visibility from a parent component,
 * typically to show controls on mouse movement within a container.
 *
 * @param autoHideDelay - Delay in ms before auto-hiding (default: 3000)
 * @returns Object with visibility state and control functions
 *
 * @example
 * ```tsx
 * const { visible, show, preventHide } = usePdfZoomControlsVisibility(3000);
 *
 * <div onMouseMove={show}>
 *   <PdfZoomControls
 *     visible={visible}
 *     onZoomIn={handleZoomIn}
 *     onZoomOut={handleZoomOut}
 *     onZoomToFit={handleZoomToFit}
 *   />
 * </div>
 * ```
 */
export const usePdfZoomControlsVisibility = (autoHideDelay = 3000) => {
  const [visible, setVisible] = useState(false);
  const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
  }, []);

  const show = useCallback(() => {
    clearHideTimeout();
    setVisible(true);
    hideTimeout.current = setTimeout(() => {
      setVisible(false);
    }, autoHideDelay);
  }, [autoHideDelay, clearHideTimeout]);

  const hide = useCallback(() => {
    setVisible(false);
    clearHideTimeout();
  }, [clearHideTimeout]);

  const preventHide = useCallback(() => {
    clearHideTimeout();
  }, [clearHideTimeout]);

  return {
    visible,
    show,
    hide,
    preventHide,
  };
};

export default PdfZoomControls;
